/**
 * O fluxo da assinatura com certificado digital (E164.F2, ADR-0113 §1): conectar ao Assinador
 * SERPRO, obter o PDF do servidor (preparar, ou o PDF da vez do responsável), assinar no programa
 * local e mandar o assinado de volta. A conferência é toda do servidor; a tela só conduz e diz o
 * que aconteceu.
 */
import type { AxiosInstance } from 'axios';

import { ptBR } from '@/i18n/pt-BR';
import { AssinadorError, conectarAssinador, type Assinador } from '@/lib/assinador_serpro';
import {
  AssinaturaIcpHttpError,
  concluirAssinatura,
  pdfDaVez,
  prepararAssinatura,
  type EstadoDaAssinaturaIcp,
} from '@/lib/assinatura_icp';
import type { ChecklistRevisao } from '@/lib/certificacao';

const t = ptBR.assinaturaIcp;

export type EtapaDaAssinatura = 'preparando' | 'no_assinador' | 'conferindo';

export interface PedidoDeAssinatura {
  client: AxiosInstance;
  requisicaoId: string;
  /** Com o checklist, prepara a emissão; sem ele, assina o PDF da vez (o responsável). */
  checklist?: ChecklistRevisao;
  conectar?: () => Promise<Assinador>;
  onEtapa?: (etapa: EtapaDaAssinatura) => void;
}

export async function assinarComOCertificado(
  pedido: PedidoDeAssinatura,
): Promise<EstadoDaAssinaturaIcp> {
  const { client, requisicaoId, checklist } = pedido;
  const avisar = pedido.onEtapa ?? (() => undefined);
  const assinador = await (pedido.conectar ?? conectarAssinador)();
  try {
    avisar('preparando');
    const pdf =
      checklist === undefined
        ? await pdfDaVez(client, requisicaoId)
        : await prepararAssinatura(client, requisicaoId, checklist);
    avisar('no_assinador');
    const assinado = await assinador.assinarPdf(pdf.pdf_base64);
    avisar('conferindo');
    return await concluirAssinatura(client, requisicaoId, assinado);
  } finally {
    assinador.fechar();
  }
}

/** A frase da tela para a falha, com a ação que resolve. */
export function mensagemDaFalha(err: unknown): string {
  if (err instanceof AssinadorError) {
    if (err.motivo === 'ausente') return t.assinadorAusente;
    if (err.motivo === 'cancelado') return t.cancelado;
    if (err.motivo === 'tempo') return t.tempoEsgotado;
    return t.assinadorRecusou.replace('{motivo}', err.message);
  }
  if (err instanceof AssinaturaIcpHttpError) {
    if (err.codigo === 'rede') return ptBR.certificacao.erroRede;
    return t.recusa[err.codigo] ?? ptBR.certificacao.erroGenerico;
  }
  return ptBR.certificacao.erroGenerico;
}

export function rotuloDaEtapa(etapa: EtapaDaAssinatura): string {
  if (etapa === 'preparando') return t.preparando;
  if (etapa === 'no_assinador') return t.noAssinador;
  return t.conferindo;
}

export function mensagemDoEstado(estado: EstadoDaAssinaturaIcp): string {
  if (estado.reconciliada) return t.reconciliada;
  return estado.estado === 'assinada' ? t.assinadaFinal : t.assinadaNaPrimeiraEtapa;
}
